'use client';

import { useCallback, useRef } from 'react';
import { noop } from 'utils/noop';
import { useIsomorphicLayoutEffect } from '../useIsomorphicLayoutEffect';

/**
 * Represents the signature of the callback function passed to `useStableCallback`
 * that allows variadic unknown arguments and a generic return type
 */
type CallbackFnType<T extends unknown[], R> = (...args: T) => R;

/**
 * takes in a callback function, puts it into a ref and returns a render-stable
 * callback that calls that ref's current value
 * @param callback - the callback to wrap in a ref
 */
export function useStableCallback<T extends unknown[], R>(
  callback: CallbackFnType<T, R>
): CallbackFnType<T, R> {
  /**
   * The type signature intentionally requires a function so that TypeScript
   * catches missing callbacks at compile time. At runtime, however, callers
   * may still pass `undefined` (e.g. via optional props). Rather than
   * crashing, we fall back to a no-op — but the real fix belongs at the
   * call site, not here.
   */
  const fallback = noop as unknown as CallbackFnType<T, R>;
  const callbackRef = useRef<CallbackFnType<T, R>>(callback ?? fallback);

  useIsomorphicLayoutEffect(() => {
    callbackRef.current = callback ?? fallback;
  });

  return useCallback((...args: T) => callbackRef.current!(...args), []);
}
